import './coapplicantPersonalDetailsPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, lowerCase, toString, trim } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { PhoneNumberInput } from '../../../components/shared_new/PhoneNumberInput/PhoneNumberInput';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';
import {
  emailValidator,
  nameValidator,
} from '../../../utils/component/inputValidators';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { userEmailAddress, userPhoneNumber },
  } = useUserStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantFirstName,
    coapplicantLastName,
    coapplicantPrimaryEmailAddress,
    coapplicantPrimaryPhoneNumber,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    createCoapplicant,
    setCoapplicantFirstName,
    setCoapplicantLastName,
    setCoapplicantPrimaryEmailAddress,
    setCoapplicantPrimaryPhoneNumber,
    syncCoapplicant,
  } = useCoapplicantStore();

  const form = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: coapplicantFirstName,
      lastNameValue: coapplicantLastName,
      primaryEmailAddressValue: coapplicantPrimaryEmailAddress,
      primaryPhoneNumberValue: inputEventHandler.phoneNumber.formatter(
        coapplicantPrimaryPhoneNumber,
      ),
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = form;

  const firstNameValidation = {
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: emailValidator,
      message: t('validation.emailAddress.invalid'),
    },
    validate: (value: string): string | boolean =>
      lowerCase(value) !== lowerCase(userEmailAddress) ||
      t('validation.emailAddress.applicantConflict'),
  };

  const phoneNumberValidation = (value: string): string | boolean =>
    lowerCase(value) !== lowerCase(userPhoneNumber) ||
    t('validation.phone.applicantConflict');

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
    } = values;

    if (
      !firstNameValue ||
      !lastNameValue ||
      !primaryEmailAddressValue ||
      !primaryPhoneNumberValue
    ) {
      return;
    }

    setCoapplicantFirstName(firstNameValue);
    setCoapplicantLastName(lastNameValue);
    setCoapplicantPrimaryEmailAddress(primaryEmailAddressValue);
    setCoapplicantPrimaryPhoneNumber(primaryPhoneNumberValue);

    if (!isDirty) {
      navigate(next());

      return;
    }

    if (isCoapplicantPersisted) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    const created = await createCoapplicant();
    if (created) {
      navigate(next());
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-personal-details-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.coapplicantPersonalDetails.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantPersonalDetails.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantPersonalDetails.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantPersonalDetails.progress}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantPersonalDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantPersonalDetails.form.h1}
              >
                {t('CoapplicantPersonalDetailsPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantPersonalDetails.form.h2}
              >
                {t('CoapplicantPersonalDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantPersonalDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-inputs">
                  <div className="input-half">
                    <TextField
                      {...register('firstNameValue', firstNameValidation)}
                      error={!isEmpty(errors.firstNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantPersonalDetails.form
                            .firstNameInput,
                      }}
                      label={t('CoapplicantPersonalDetailsPage.firstName')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.firstNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantPersonalDetails.form
                            .firstNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.firstNameValue.message)}
                      </span>
                    )}
                  </div>

                  <div className="input-half">
                    <TextField
                      {...register('lastNameValue', lastNameValidation)}
                      error={!isEmpty(errors.lastNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantPersonalDetails.form
                            .lastNameInput,
                      }}
                      label={t('CoapplicantPersonalDetailsPage.lastName')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.lastNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantPersonalDetails.form
                            .lastNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.lastNameValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <PhoneNumberInput
                    form={form}
                    inputLabelKey={'CoapplicantPersonalDetailsPage.phone'}
                    inputName="primaryPhoneNumberValue"
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantPersonalDetails.form
                          .phoneInput,
                    }}
                    validate={phoneNumberValidation}
                    value={coapplicantPrimaryPhoneNumber}
                  />

                  {!isEmpty(errors.primaryPhoneNumberValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantPersonalDetails.form
                          .phoneInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.primaryPhoneNumberValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'primaryEmailAddressValue',
                      emailAddressValidation,
                    )}
                    defaultValue={coapplicantPrimaryEmailAddress}
                    error={!isEmpty(errors.primaryEmailAddressValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantPersonalDetails.form
                          .emailAddressInput,
                    }}
                    label={t('CoapplicantPersonalDetailsPage.email')}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.primaryEmailAddressValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantPersonalDetails.form
                          .emailAddressInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.primaryEmailAddressValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantPersonalDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantPersonalDetailsPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantPersonalDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantPersonalDetailsPage';
